import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Brain,
  Stethoscope,
  MapPin,
  Users,
  HeartPulse,
  Share,
  X,
} from "lucide-react";

function IOSInstallBanner() {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const userAgent = window.navigator.userAgent || "";
    const isIOS = /iPad|iPhone|iPod/.test(userAgent);

    const isStandalone =
      window.navigator.standalone === true ||
      window.matchMedia("(display-mode: standalone)").matches;

    const dismissed = localStorage.getItem("ios-install-banner-dismissed");

    if (isIOS && !isStandalone && dismissed !== "yes") {
      setShowBanner(true);
    }
  }, []);

  const dismissBanner = () => {
    localStorage.setItem("ios-install-banner-dismissed", "yes");
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div className="mb-6 rounded-[1.5rem] border border-[#D7E7F6] bg-white/90 backdrop-blur-xl shadow-lg p-4">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-2xl bg-[#EEF5FB] flex items-center justify-center shrink-0">
          <Share className="w-5 h-5 text-[#356DFF]" />
        </div>

        <div className="flex-1">
          <h3 className="font-bold text-slate-900 text-sm">
            Install PinMyDoc on your iPhone
          </h3>

          <p className="mt-1 text-xs leading-6 text-slate-600">
            Tap the browser share button, then choose{" "}
            <span className="font-semibold text-slate-800">
              Add to Home Screen
            </span>{" "}
            to use it like an app.
          </p>
        </div>

        <button
          type="button"
          onClick={dismissBanner}
          className="text-slate-400 hover:text-slate-700"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

export default function Home() {
  const [stats, setStats] = useState({
    doctors: 0,
    clinics: 0,
    cities: 0,
    reports: 0,
  });

  useEffect(() => {
    fetch("https://app.techdroid.ca/home_stats.php")
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.stats) {
          setStats(data.stats);
        }
      })
      .catch(() => {});
  }, []);

  return (
    <div className="relative overflow-hidden min-h-screen">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(53,109,255,0.18),transparent_30%),radial-gradient(circle_at_80%_40%,rgba(139,92,246,0.16),transparent_32%),linear-gradient(135deg,#EEF5FB,#F8FAFC,#F1F5F9)]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-20">
        <IOSInstallBanner />

        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-white/80 backdrop-blur border border-slate-200 px-4 py-2 shadow-md text-sm text-slate-600 font-medium">
              <Brain className="w-4 h-4 text-[#6B6BFF]" />
              PinMyDoc care discovery platform
            </div>

            <h1 className="mt-8 text-5xl sm:text-6xl leading-[1.02] font-black tracking-tight text-slate-900">
              Find family doctors and clinics{" "}
              <span className="text-[#356DFF]">
                accepting patients in Ontario.
              </span>
            </h1>

            <p className="mt-6 text-lg leading-9 text-slate-600 max-w-xl">
              Browse family doctors, walk-in clinics, pediatric care, and
              primary care providers using public information and community
              availability signals.
            </p>

            <div className="mt-10 bg-white/80 backdrop-blur border border-white rounded-[2rem] p-4 shadow-2xl max-w-xl">
              <Link
                to="/doctors"
                className="w-full inline-flex items-center justify-center gap-2 rounded-2xl py-4 font-semibold bg-[#356DFF] text-white hover:bg-[#255AE6] transition shadow-lg"
              >
                Find Care in Ontario
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="mt-5 rounded-3xl border border-slate-200 bg-white/60 backdrop-blur px-5 py-4 text-sm text-slate-500 max-w-xl">
              Availability status is based on public information and community
              reports. Please contact the clinic directly to confirm whether
              they are currently accepting new patients.
            </div>

            <div className="relative mt-10 rounded-[38px] p-5 overflow-hidden border border-white/80 bg-gradient-to-br from-[#DCEBFF]/70 via-white/35 to-[#EFE7FF]/70 backdrop-blur-2xl shadow-[0_25px_80px_rgba(53,109,255,0.18)]">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.18),transparent_30%),radial-gradient(circle_at_bottom_right,rgba(168,85,247,0.18),transparent_30%)]" />

              <div className="relative grid grid-cols-1 md:grid-cols-3 gap-5">
                <div className="relative overflow-hidden rounded-[30px] bg-white/45 backdrop-blur-2xl border border-white/90 shadow-[0_18px_55px_rgba(15,23,42,0.12)] min-h-[330px]">
                  <div className="h-[5px] w-full bg-[#356DFF]" />

                  <div className="p-6 flex flex-col h-full">
                    <div className="w-14 h-14 rounded-2xl bg-[#EEF4FF] flex items-center justify-center mb-6">
                      <Stethoscope className="w-7 h-7 text-[#356DFF]" />
                    </div>

                    <div className="flex items-end gap-1">
                      <h2 className="text-[52px] font-black leading-none text-[#0F172A]">
                        {stats.doctors || 0}
                      </h2>
                      <span className="text-[26px] font-bold text-[#94A3B8] mb-1">
                        +
                      </span>
                    </div>

                    <h3 className="text-[18px] font-bold text-[#0F172A] mt-2">
                      Family Doctors
                    </h3>

                    <div className="h-px bg-slate-200/80 my-5"></div>

                    <p className="text-[15px] leading-8 text-slate-600">
                      Verified public provider records across Ontario
                    </p>

                    <div className="mt-auto flex flex-wrap gap-3 pt-6">
                      <span className="px-4 py-2 rounded-full bg-white/75 border border-slate-200 text-[13px] font-semibold text-slate-700 shadow-sm">
                        Walk-In
                      </span>

                      <span className="px-4 py-2 rounded-full bg-white/75 border border-slate-200 text-[13px] font-semibold text-slate-700 shadow-sm">
                        Primary care
                      </span>
                    </div>
                  </div>
                </div>

                <div className="relative overflow-hidden rounded-[30px] bg-white/45 backdrop-blur-2xl border border-white/90 shadow-[0_18px_55px_rgba(15,23,42,0.12)] min-h-[330px]">
                  <div className="h-[5px] w-full bg-[#F97316]" />

                  <div className="p-6 flex flex-col h-full">
                    <div className="w-14 h-14 rounded-2xl bg-[#FFF4EC] flex items-center justify-center mb-6">
                      <Users className="w-7 h-7 text-[#F97316]" />
                    </div>

                    <div className="flex items-end gap-2 flex-wrap">
                      <h2 className="text-[50px] font-black leading-none text-[#0F172A]">
                        Live
                      </h2>
                      <span className="text-[20px] font-bold text-[#94A3B8] mb-1">
                        updates
                      </span>
                    </div>

                    <h3 className="text-[18px] font-bold text-[#0F172A] mt-3">
                      Community Reports
                    </h3>

                    <div className="h-px bg-slate-200/80 my-5"></div>

                    <p className="text-[15px] leading-8 text-slate-600">
                      Real availability signals from community members
                    </p>

                    <div className="mt-auto flex flex-wrap gap-3 pt-6">
                      <span className="px-4 py-2 rounded-full bg-white/75 border border-slate-200 text-[13px] font-semibold text-slate-700 shadow-sm">
                        Crowdsourced
                      </span>

                      <span className="px-4 py-2 rounded-full bg-white/75 border border-slate-200 text-[13px] font-semibold text-slate-700 shadow-sm">
                        Up to date
                      </span>
                    </div>
                  </div>
                </div>

                <div className="relative overflow-hidden rounded-[30px] bg-white/45 backdrop-blur-2xl border border-white/90 shadow-[0_18px_55px_rgba(15,23,42,0.12)] min-h-[330px]">
                  <div className="h-[5px] w-full bg-[#8B5CF6]" />

                  <div className="p-6 flex flex-col h-full">
                    <div className="w-14 h-14 rounded-2xl bg-[#F4EEFF] flex items-center justify-center mb-6">
                      <MapPin className="w-7 h-7 text-[#8B5CF6]" />
                    </div>

                    <div className="flex items-end gap-1 flex-wrap">
                      <h2 className="text-[52px] font-black leading-none text-[#0F172A]">
                        {stats.cities || 0}
                      </h2>
                      <span className="text-[26px] font-bold text-[#94A3B8] mb-1">
                        +
                      </span>
                      <span className="text-[20px] font-bold text-[#94A3B8] mb-2">
                        cities
                      </span>
                    </div>

                    <h3 className="text-[18px] font-bold text-[#0F172A] mt-2">
                      Ontario Coverage
                    </h3>

                    <div className="h-px bg-slate-200/80 my-5"></div>

                    <p className="text-[15px] leading-8 text-slate-600">
                      Growing across major Ontario regions and municipalities
                    </p>

                    <div className="mt-auto flex flex-wrap gap-3 pt-6">
                      <span className="px-4 py-2 rounded-full bg-white/75 border border-slate-200 text-[13px] font-semibold text-slate-700 shadow-sm">
                        Toronto
                      </span>

                      <span className="px-4 py-2 rounded-full bg-white/75 border border-slate-200 text-[13px] font-semibold text-slate-700 shadow-sm">
                        More cities
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="bg-white/70 backdrop-blur rounded-[2.5rem] border border-white shadow-2xl p-6">
              <div className="flex items-center justify-between gap-6">
                <div>
                  <div className="text-sm text-slate-500">
                    Care access tools
                  </div>

                  <div className="text-3xl font-bold text-slate-900 mt-1">
                    PinMyDoc Hub
                  </div>
                </div>

                <div className="w-14 h-14 rounded-3xl bg-white shadow-md border border-slate-100 flex items-center justify-center shrink-0">
                  <HeartPulse
                    className="w-7 h-7 text-[#F97316]"
                    strokeWidth={2.4}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-8">
                <div className="bg-white rounded-3xl p-5 shadow-md">
                  <Brain className="w-8 h-8 text-[#356DFF]" />

                  <div className="mt-4 text-sm text-slate-500">Search by</div>

                  <div className="text-2xl font-bold text-slate-900 mt-1">
                    Category
                  </div>
                </div>

                <div className="bg-white rounded-3xl p-5 shadow-md">
                  <Stethoscope className="w-8 h-8 text-[#6B6BFF]" />

                  <div className="mt-4 text-sm text-slate-500">Find</div>

                  <div className="text-2xl font-bold text-slate-900 mt-1">
                    Family Doctors
                  </div>
                </div>

                <div className="bg-white rounded-3xl p-5 shadow-md">
                  <Users className="w-8 h-8 text-[#16B364]" />

                  <div className="mt-4 text-sm text-slate-500">Community</div>

                  <div className="text-2xl font-bold text-slate-900 mt-1">
                    Reports
                  </div>
                </div>

                <div className="bg-white rounded-3xl p-5 shadow-md">
                  <MapPin className="w-8 h-8 text-[#F97316]" />

                  <div className="mt-4 text-sm text-slate-500">Explore</div>

                  <div className="text-2xl font-bold text-slate-900 mt-1">
                    Cities
                  </div>
                </div>
              </div>

              <div className="mt-6 rounded-3xl bg-gradient-to-r from-[#EEF5FB] to-[#F8FAFC] border border-slate-200 p-5 text-sm text-slate-500">
                Designed to help residents discover family doctors, walk-in
                clinics, pediatric care, and primary care options.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}